import { App } from "antd"
import dayjs from "dayjs"
import { useCallback, useMemo, useState } from "react"

import { tryoutAtoms } from "#atoms"
import GenericFormModal from "#blocks/Form/GenericFormModal"
import Firebase, { NULL_ID } from "#classes/Firebase"
import { ANALYTICS_EVENTS } from "#classes/FirebaseAnalytics"
import { TRYOUT_FEES } from "#constants/tryoutOptions"
import { useApiContext } from "#contexts/ApiContextProvider"
import { RESOURCES } from "#customTypes/composedTypes/authTypes"
import { ClassSessionConstructor } from "#customTypes/composedTypes/classSessionTypes"
import {
  AddTryoutReceipt,
  TRYOUT_RECEIPT_TYPES,
} from "#customTypes/composedTypes/emailTypes"
import { DOC_TYPES } from "#customTypes/composedTypes/firebaseTypes"
import { StudentConstructor } from "#customTypes/composedTypes/studentTypes"
import {
  type AddTryoutFormSubmitValues,
  type AddTryoutFormValues,
  TryoutConstructor,
} from "#customTypes/composedTypes/tryoutTypes"
import { Uuid } from "#customTypes/globalTypes"
import { invariant } from "#customTypes/types"
import { useOneDoc } from "#hooks/jotai/useSubscription"
import { useResourcePermissions } from "#hooks/newHooks/useHasPermission"
import useAnalytics from "#hooks/useAnalytics"
import {
  getNow,
  STANDARD_DATE_FORMAT,
  STANDARD_DATE_TIME_FORMAT,
  STANDARD_DAY_FORMAT,
  STANDARD_TIME_FORMAT,
} from "#utils/dateHelpers"
import { getStudentRecipients } from "#utils/emailHelpers"
import { getCurrentSemester } from "#utils/semesterHelpers"

import AddTryoutForm from "./AddTryoutForm"

const DEFAULT_TRYOUT_VALUES: AddTryoutFormValues = {
  id: undefined,
  studentId: undefined,
  classId: undefined,
  sessionId: undefined,
  tryoutFilterDate: getNow().startOf("day"),
  notes: "",
  semester: getCurrentSemester(),
  step0: {
    tryoutFeeId: undefined,
    isTryoutFeePaid: true,
    hasSelectedSession: false,
  },
}

const convertTryoutToAddTryoutFormValues = (
  tryout: TryoutConstructor | null | undefined
): AddTryoutFormValues | undefined => {
  if (!tryout) return DEFAULT_TRYOUT_VALUES

  return {
    ...tryout,
    tryoutFilterDate: tryout.tryoutFilterDate
      ? dayjs(tryout.tryoutFilterDate)
      : DEFAULT_TRYOUT_VALUES.tryoutFilterDate,
  }
}

const saveCreatedTryout = async (
  values: AddTryoutFormSubmitValues
): Promise<void> => {
  await Firebase.getInstance().createTryout(values)
}

export interface AddTryoutFormModalProps {
  onClose: () => void
  tryoutId: Uuid | undefined
}

const AddTryoutFormModal = ({ onClose, tryoutId }: AddTryoutFormModalProps) => {
  const isEdit = tryoutId != null
  const { trackTryoutAction } = useAnalytics()
  const [sendReceipt, setSendReceipt] = useState(true)

  const { canCreate, canRead, canUpdate } = useResourcePermissions(
    RESOURCES.TRYOUT
  )

  const tryout = useOneDoc<TryoutConstructor>(
    tryoutAtoms,
    DOC_TYPES.TRYOUTS,
    (canRead && tryoutId) || NULL_ID
  )

  const initialValues = useMemo(() => {
    if (!tryout || !canRead) return DEFAULT_TRYOUT_VALUES
    return convertTryoutToAddTryoutFormValues(tryout)
  }, [tryout, canRead])

  const { apiHandler } = useApiContext()
  const { message } = App.useApp()

  // Errors handled in handleSubmit
  const handleSendReceipt = useCallback(
    async (formValues: AddTryoutFormValues) => {
      const { studentId, sessionId } = formValues
      const tryoutFeeId = formValues.step0?.tryoutFeeId

      invariant(
        (typeof studentId === "string") === true,
        `Data for _student_id ${studentId} is missing!`
      )
      invariant(
        (typeof sessionId === "string") === true,
        `Data for _session_id ${sessionId} is missing!`
      )

      if (!tryoutFeeId) {
        throw new Error("Tryout fee is required for a receipt.")
      }

      const selectedStudent =
        await Firebase.getInstance().getDocumentById<StudentConstructor>(
          DOC_TYPES.STUDENTS,
          studentId
        )

      if (!selectedStudent) {
        throw new Error("Student not found.")
      }

      const session =
        await Firebase.getInstance().getDocumentById<ClassSessionConstructor>(
          DOC_TYPES.CLASS_SESSIONS,
          sessionId
        )

      if (!session) {
        throw new Error("Session not found.")
      }

      const recipients = getStudentRecipients(selectedStudent)

      if (!recipients.length) {
        throw new Error("No recipients found.")
      }

      const sessionDate = dayjs(session.eventStart).format(STANDARD_DATE_FORMAT)
      const sessionDay = dayjs(session.eventStart).format(STANDARD_DAY_FORMAT)
      const sessionStartTime = dayjs(session.eventStart).format(
        STANDARD_TIME_FORMAT
      )
      const sessionEndTime = dayjs(session.eventEnd).format(
        STANDARD_TIME_FORMAT
      )

      const receiptData: AddTryoutReceipt = {
        recipients,
        sendTime: dayjs().format(STANDARD_DATE_TIME_FORMAT),

        studentName: selectedStudent.name,
        studentDob: dayjs(selectedStudent.dob).format(STANDARD_DATE_FORMAT),

        sessionDate,
        sessionDay,
        sessionStartTime,
        sessionEndTime,
        // TODO: Format this
        tryoutFees: tryoutFeeId
          ? `${TRYOUT_FEES.find((fee) => fee.id === tryoutFeeId)?.label ?? ""}`
          : "",
        isTryoutFeePaid: formValues.step0?.isTryoutFeePaid ?? false,
      }

      await apiHandler.sendReceiptEmail(
        receiptData,
        TRYOUT_RECEIPT_TYPES.TRYOUT_CONFIRMATION
      )
      message.success("Tryout confirmation email sent")
    },
    [apiHandler, message]
  )

  // Errors are handled in GenericFormModal
  const handleFinish = async (
    cleanedValues: AddTryoutFormValues
  ): Promise<void> => {
    if (!canCreate && !isEdit) {
      throw new Error("You do not have permission to create tryouts")
    }
    if (!canUpdate && isEdit) {
      throw new Error("You do not have permission to update tryouts")
    }

    const { receipt, ...otherValues } = cleanedValues

    invariant(
      (typeof otherValues.studentId === "string") === true,
      `Data for _student_id ${otherValues.studentId} is missing!`
    )
    invariant(
      (typeof otherValues.sessionId === "string") === true,
      `Data for _session_id ${otherValues.sessionId} is missing!`
    )
    invariant(
      (typeof otherValues.semester === "string") === true,
      `Data for semester is missing!`
    )

    // Create payload is step0 + identity fields only. Do not invent
    // tryoutStatus / step1 / step2 / step3 for the constructor type.
    const submitData: AddTryoutFormSubmitValues = {
      ...otherValues,
      studentId: otherValues.studentId,
      sessionId: otherValues.sessionId,
      tryoutFilterDate: (otherValues.tryoutFilterDate ?? dayjs()).toISOString(),
      semester: otherValues.semester,
      step0: {
        isTryoutFeePaid: otherValues.step0?.isTryoutFeePaid ?? false,
        tryoutFeeId: otherValues.step0?.tryoutFeeId,
      },
    }

    await saveCreatedTryout(submitData)

    invariant(
      (typeof submitData.id === "string") === true,
      `Data for _tryout_id ${submitData.id} is missing!`
    )

    trackTryoutAction(ANALYTICS_EVENTS.TRYOUT_SCHEDULED, {
      tryoutId: submitData.id,
      studentId: submitData.studentId,
      sessionId: submitData.sessionId,
    })

    if (sendReceipt) {
      await handleSendReceipt(cleanedValues)
    }
  }

  return (
    <GenericFormModal<AddTryoutFormValues, AddTryoutFormValues>
      title={isEdit ? "Edit Tryout" : "Add Tryout"}
      onClose={onClose}
      initialValues={initialValues}
      onFinish={handleFinish}
      resource={RESOURCES.TRYOUT}
      // Once a tryout is created, it's shown in TryoutProgressForm
      onDelete={Promise.resolve}
    >
      <AddTryoutForm
        initialValues={initialValues}
        sendReceipt={sendReceipt}
        onSendReceiptChange={setSendReceipt}
      />
    </GenericFormModal>
  )
}

export default AddTryoutFormModal
